/* eslint-disable no-console */
/**
 * Tool Result Registry
 *
 * Centralized registry for handling tool results from Solomon agent.
 * Components can register handlers for specific tools, and the registry
 * routes results to the appropriate handlers.
 *
 * Follows Unix philosophy: one file, one job.
 *
 * @example
 * // Register a handler
 * const unregister = toolRegistry.register('generate_image', (result) => {
 *   console.log('Image generated:', result);
 * });
 *
 * // Handle a tool result
 * toolRegistry.handle('generate_image', { imageUrl: '...' });
 *
 * // Cleanup
 * unregister();
 */

// ============================================================================
// Types
// ============================================================================

/** Handler function for a tool result */
// ============================================================================
// Hook for React Components
// ============================================================================

import { useEffect } from 'react';

export type ToolResultHandler<T = unknown> = (result: T, metadata?: ToolResultMetadata) => void;

/** Metadata about the tool result */
export interface ToolResultMetadata {
  /** Tool call ID for tracing */
  toolCallId?: string;
  /** Whether the result is an error */
  isError?: boolean;
  /** Tool input parameters */
  input?: Record<string, unknown>;
}

// ============================================================================
// Registry Class
// ============================================================================

class ToolResultRegistry {
  private handlers = new Map<string, Set<ToolResultHandler>>();

  /**
   * Register a handler for a specific tool.
   * @param toolName - Name of the tool to handle
   * @param handler - Handler function to call when tool result arrives
   * @returns Unregister function
   */
  register<T = unknown>(toolName: string, handler: ToolResultHandler<T>): () => void {
    if (!this.handlers.has(toolName)) {
      this.handlers.set(toolName, new Set());
    }

    const handlers = this.handlers.get(toolName)!;
    handlers.add(handler as ToolResultHandler);

    console.log(`[toolRegistry] Registered handler for "${toolName}", total: ${handlers.size}`);

    // Return unregister function
    return () => {
      handlers.delete(handler as ToolResultHandler);
      console.log(`[toolRegistry] Unregistered handler for "${toolName}", remaining: ${handlers.size}`);
    };
  }

  /**
   * Handle a tool result by calling all registered handlers.
   * @param toolName - Name of the tool
   * @param result - Tool result data
   * @param metadata - Optional metadata about the result
   */
  handle<T = unknown>(toolName: string, result: T, metadata?: ToolResultMetadata): void {
    const handlers = this.handlers.get(toolName);

    if (!handlers || handlers.size === 0) {
      console.log(`[toolRegistry] No handlers for "${toolName}"`, { result, metadata });
      return;
    }

    console.log(`[toolRegistry] Handling "${toolName}" with ${handlers.size} handler(s)`, {
      resultType: typeof result,
      metadata,
    });

    handlers.forEach((handler) => {
      try {
        handler(result, metadata);
      } catch (error) {
        console.error(`[toolRegistry] Handler error for "${toolName}":`, error);
      }
    });
  }

  /**
   * Check if there are any handlers for a tool.
   * @param toolName - Name of the tool
   * @returns Whether handlers exist
   */
  hasHandlers(toolName: string): boolean {
    const handlers = this.handlers.get(toolName);
    return handlers !== undefined && handlers.size > 0;
  }

  /**
   * Get the number of handlers for a tool.
   * @param toolName - Name of the tool
   * @returns Number of handlers
   */
  getHandlerCount(toolName: string): number {
    return this.handlers.get(toolName)?.size ?? 0;
  }

  /**
   * Clear all handlers for a tool.
   * @param toolName - Name of the tool
   */
  clearHandlers(toolName: string): void {
    this.handlers.delete(toolName);
    console.log(`[toolRegistry] Cleared all handlers for "${toolName}"`);
  }

  /**
   * Clear all handlers.
   */
  clearAll(): void {
    this.handlers.clear();
    console.log('[toolRegistry] Cleared all handlers');
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

/** Global tool result registry instance */
export const toolRegistry = new ToolResultRegistry();

/**
 * React hook to register a tool result handler.
 * Automatically unregisters when component unmounts.
 *
 * @param toolName - Name of the tool to handle
 * @param handler - Handler function
 * @param deps - Dependencies array (like useEffect)
 */
export function useToolResultHandler<T = unknown>(
  toolName: string,
  handler: ToolResultHandler<T>,
  deps: React.DependencyList = []
): void {
  useEffect(() => {
    const unregister = toolRegistry.register(toolName, handler);
    return unregister;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolName, ...deps]);
}

export default toolRegistry;
